import React, { createContext, useContext, useReducer, useCallback, useMemo, useRef } from 'react';
import { cache, CACHE_TAGS } from '../utils/cache';
import { clearMemoCache } from '../utils/statsUtils';
import { roomsApi } from '../api/rooms';
import { flocksApi } from '../api/flocks';
import { eggRecordsApi } from '../api/eggRecords';
import { mortalityApi } from '../api/mortality';
import { feedApi } from '../api/feed';
import { incubatorApi, mergeBatchChildren } from '../api/incubator';
import { salesApi } from '../api/sales';
import { expensesApi } from '../api/expenses';
import { notificationsApi } from '../api/notifications';
import { profileApi } from '../api/profile';
import { insightsApi } from '../api/insights';
import { inventoryApi } from '../api/inventory';
import { useAuth } from './AuthContext';
import { canAccessFinancialModules } from '../utils/roles';

const AppContext = createContext(null);

const _now = new Date();
const TODAY = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;

// Helper: extract fulfilled value from Promise.allSettled result, or return fallback
function ok(result, fallback = []) {
  return result.status === 'fulfilled' ? result.value : fallback;
}

// ─── Initial (empty) state ─────────────────────────────────────────────────────

const emptyState = {
  rooms:            [],
  flocks:           [],
  eggRecords:       [],
  mortalityRecords: [],
  feedRecords:      [],
  incubators:       [],
  incubatorBatches: [],
  sales:            [],
  expenses:         [],
  notifications:           [],
  insights:                [],
  inventoryItems:          [],
  selectedRoomId:          null,
  selectedFlockId:         null,
  selectedInventoryItemId: null,
  theme:            localStorage.getItem('fs_theme') || 'light',
  loading:          false,
  userProfile:      null,
};

// ─── Reducer ──────────────────────────────────────────────────────────────────

function appReducer(state, action) {
  switch (action.type) {

    // Logout: drop every tenant record so the next login (possibly a different farm/role on a
    // shared device) can never see the previous user's data, even for endpoints it can't load.
    case 'RESET_DATA':
      return { ...emptyState, theme: state.theme };

    case 'LOAD_PRIORITY_DATA':
      return { ...state, ...action.payload, loading: false };

    case 'LOAD_SECONDARY_DATA':
      return { ...state, ...action.payload };

    case 'LOAD_FINANCIAL_DATA':
      return { ...state, ...action.payload };

    case 'LOAD_USER_PROFILE':
      return { ...state, userProfile: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SELECT_ROOM':
      return { ...state, selectedRoomId: action.payload };

    case 'SELECT_FLOCK':
      return { ...state, selectedFlockId: action.payload };

    case 'TOGGLE_THEME': {
      const next = state.theme === 'light' ? 'dark' : 'light';
      localStorage.setItem('fs_theme', next);
      if (next === 'dark') document.documentElement.classList.add('dark');
      else                 document.documentElement.classList.remove('dark');
      return { ...state, theme: next };
    }

    // ── Rooms ──────────────────────────────────────────────────────────────────
    case 'ADD_ROOM':
      return { ...state, rooms: [...state.rooms, action.payload] };

    case 'UPDATE_ROOM':
      return {
        ...state,
        rooms: state.rooms.map((r) => r.id === action.payload.id ? { ...r, ...action.payload } : r),
      };

    case 'DELETE_ROOM':
      return {
        ...state,
        rooms:  state.rooms.filter((r) => r.id !== action.payload),
        flocks: state.flocks.filter((f) => f.roomId !== action.payload),
        eggRecords: state.eggRecords.filter((e) => e.roomId !== action.payload),
      };

    // ── Flocks ─────────────────────────────────────────────────────────────────
    case 'ADD_FLOCK':
      return { ...state, flocks: [...state.flocks, action.payload] };

    case 'UPDATE_FLOCK':
      return {
        ...state,
        flocks: state.flocks.map((f) => f.id === action.payload.id ? { ...f, ...action.payload } : f),
      };

    case 'DELETE_FLOCK':
      return {
        ...state,
        flocks:     state.flocks.filter((f) => f.id !== action.payload),
        eggRecords: state.eggRecords.filter((e) => e.flockId !== action.payload),
      };

    case 'SET_FLOCKS':
      return { ...state, flocks: action.payload };

    // ── Egg Records ────────────────────────────────────────────────────────────
    case 'ADD_EGG_RECORD': {
      // Dedup: one record per flock per date
      const existing = state.eggRecords.find(
        (e) => e.flockId === action.payload.flockId && e.date === action.payload.date
      );
      if (existing) {
        return {
          ...state,
          eggRecords: state.eggRecords.map((e) =>
            e.id === existing.id ? { ...e, ...action.payload } : e
          ),
        };
      }
      return { ...state, eggRecords: [...state.eggRecords, action.payload] };
    }

    case 'DELETE_EGG_RECORD':
      return { ...state, eggRecords: state.eggRecords.filter((e) => e.id !== action.payload) };

    case 'SET_EGG_RECORDS':
      return { ...state, eggRecords: action.payload };

    case 'UPDATE_EGG_ALLOCATION': {
      const { id, qtyIncubated = 0, qtySold = 0 } = action.payload;
      return {
        ...state,
        eggRecords: state.eggRecords.map((e) =>
          e.id === id
            ? { ...e, qtyIncubated: (e.qtyIncubated || 0) + qtyIncubated, qtySold: (e.qtySold || 0) + qtySold }
            : e
        ),
      };
    }

    // ── Mortality Records ──────────────────────────────────────────────────────
    case 'ADD_MORTALITY_RECORD':
      return { ...state, mortalityRecords: [...state.mortalityRecords, action.payload] };

    case 'UPDATE_MORTALITY_RECORD':
      return {
        ...state,
        mortalityRecords: state.mortalityRecords.map((r) =>
          r.id === action.payload.id ? { ...r, ...action.payload } : r
        ),
      };

    case 'DELETE_MORTALITY_RECORD':
      return { ...state, mortalityRecords: state.mortalityRecords.filter((r) => r.id !== action.payload) };

    // ── Feed Records ───────────────────────────────────────────────────────────
    case 'ADD_FEED_RECORD':
      return { ...state, feedRecords: [...state.feedRecords, action.payload] };

    case 'UPDATE_FEED_RECORD':
      return {
        ...state,
        feedRecords: state.feedRecords.map((r) =>
          r.id === action.payload.id ? { ...r, ...action.payload } : r
        ),
      };

    case 'DELETE_FEED_RECORD':
      return { ...state, feedRecords: state.feedRecords.filter((r) => r.id !== action.payload) };

    // ── Inventory ──────────────────────────────────────────────────────────────
    case 'SET_INVENTORY_ITEMS':
      return { ...state, inventoryItems: action.payload };

    case 'ADD_INVENTORY_ITEM':
      return { ...state, inventoryItems: [...state.inventoryItems, action.payload] };

    case 'UPDATE_INVENTORY_ITEM':
      return {
        ...state,
        inventoryItems: state.inventoryItems.map((i) =>
          i.id === action.payload.id ? { ...i, ...action.payload } : i
        ),
      };

    case 'DELETE_INVENTORY_ITEM':
      return { ...state, inventoryItems: state.inventoryItems.filter((i) => i.id !== action.payload) };

    case 'SELECT_INVENTORY_ITEM':
      return { ...state, selectedInventoryItemId: action.payload };

    // ── Incubator Machines ─────────────────────────────────────────────────────
    case 'ADD_INCUBATOR':
      return { ...state, incubators: [...state.incubators, action.payload] };

    case 'UPDATE_INCUBATOR':
      return {
        ...state,
        incubators: state.incubators.map((m) => m.id === action.payload.id ? { ...m, ...action.payload } : m),
      };

    case 'DELETE_INCUBATOR':
      return { ...state, incubators: state.incubators.filter((m) => m.id !== action.payload) };

    case 'SET_INCUBATORS':
      return { ...state, incubators: action.payload };

    // ── Incubator Batches ──────────────────────────────────────────────────────
    case 'ADD_INCUBATOR_BATCH':
      return {
        ...state,
        incubatorBatches: [...state.incubatorBatches, { ...action.payload, transfers: [], chickLosses: [] }],
      };

    case 'UPDATE_INCUBATOR_BATCH':
      return {
        ...state,
        incubatorBatches: state.incubatorBatches.map((b) =>
          b.id === action.payload.id ? { ...b, ...action.payload } : b
        ),
      };

    case 'DELETE_INCUBATOR_BATCH':
      return { ...state, incubatorBatches: state.incubatorBatches.filter((b) => b.id !== action.payload) };

    case 'ADD_BATCH_TRANSFER': {
      const { batchId, transfer } = action.payload;
      return {
        ...state,
        incubatorBatches: state.incubatorBatches.map((b) =>
          b.id === batchId
            ? { ...b, transfers: [...(b.transfers || []), transfer] }
            : b
        ),
        // Flock counts are updated by DB trigger; reload flocks after transfer via action
      };
    }

    case 'ADD_CHICK_LOSS': {
      const { batchId, loss } = action.payload;
      return {
        ...state,
        incubatorBatches: state.incubatorBatches.map((b) =>
          b.id === batchId
            ? { ...b, chickLosses: [...(b.chickLosses || []), loss] }
            : b
        ),
      };
    }

    // ── Sales ──────────────────────────────────────────────────────────────────
    case 'ADD_SALE':
      return { ...state, sales: [action.payload, ...state.sales] };

    case 'DELETE_SALE':
      return { ...state, sales: state.sales.filter((s) => s.id !== action.payload) };

    case 'EDIT_SALE': {
      const { id, ...updates } = action.payload;
      return {
        ...state,
        sales: state.sales.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      };
    }

    // ── Expenses ───────────────────────────────────────────────────────────────
    case 'ADD_EXPENSE':
      return { ...state, expenses: [action.payload, ...state.expenses] };

    case 'UPDATE_EXPENSE':
      return { ...state, expenses: state.expenses.map((e) => (e.id === action.payload.id ? { ...e, ...action.payload } : e)) };

    case 'DELETE_EXPENSE':
      return { ...state, expenses: state.expenses.filter((e) => e.id !== action.payload) };

    // ── Notifications ──────────────────────────────────────────────────────────
    case 'LOAD_NOTIFICATIONS':
      return { ...state, notifications: action.payload };

    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.id === action.payload ? { ...n, read: true } : n
        ),
      };

    case 'MARK_ALL_NOTIFICATIONS_READ':
      return {
        ...state,
        notifications: state.notifications.map((n) => ({ ...n, read: true })),
      };

    case 'REMOVE_NOTIFICATION':
      return { ...state, notifications: state.notifications.filter((n) => n.id !== action.payload) };

    // ── Insights ───────────────────────────────────────────────────────────────
    case 'LOAD_INSIGHTS':
      return { ...state, insights: action.payload };

    case 'ADD_INSIGHT':
      return { ...state, insights: [action.payload, ...state.insights] };

    case 'UPDATE_INSIGHT':
      return {
        ...state,
        insights: state.insights.map((i) =>
          i.id === action.payload.id ? { ...i, ...action.payload } : i
        ),
      };

    case 'MARK_INSIGHT_NOTIFIED':
      return {
        ...state,
        insights: state.insights.map((i) =>
          i.id === action.payload ? { ...i, isNotified: true } : i
        ),
      };

    default:
      return state;
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, emptyState);
  const { user } = useAuth();
  const canLoadFinancial = canAccessFinancialModules(user?.role);

  // Apply saved theme on mount
  React.useEffect(() => {
    if (state.theme === 'dark') document.documentElement.classList.add('dark');
    else                        document.documentElement.classList.remove('dark');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track if initial load has been attempted
  const initialLoadRef = useRef(false);

  // ── Load all data from API ─────────────────────────────────────────────────
  // OPTIMIZED: Staged loading with priority (critical first, deferrable second)
  const loadAllData = useCallback(async (options = {}) => {
    const { priorityOnly = false, background = false } = options;

    if (!background) {
      dispatch({ type: 'SET_LOADING', payload: true });
    }

    try {
      // All three stages are independent requests, so start them together instead of as a
      // 3-step waterfall (stage 2 used to wait for stage 1, stage 3 for stage 2 = 3 serial
      // round-trips before the app was fully populated). Each stage still dispatches the
      // moment its own data lands, so priority data (rooms/flocks) still paints first.
      const priorityP = Promise.allSettled([
        roomsApi.getAll(),
        flocksApi.getAll(),
        profileApi.getMe(),
      ]);

      // Stage 2/3 are only started when requested (priorityOnly skips them entirely).
      const secondaryP = priorityOnly ? null : Promise.allSettled([
        eggRecordsApi.getAll(),
        mortalityApi.getAll(),
        feedApi.getAll(),
        incubatorApi.getAllMachines(),
        incubatorApi.getAllBatches(),
        incubatorApi.getAllTransfers(),
        incubatorApi.getAllChickLosses(),
        notificationsApi.getAll(),
        insightsApi.getActive(7),
        inventoryApi.getAllItems(),
      ]);

      // Financial data (only for authorized roles)
      const financialP = priorityOnly || !canLoadFinancial ? null : Promise.allSettled([
        salesApi.getAll(),
        expensesApi.getAll(),
      ]);

      const [rooms, flocks, profile] = await priorityP;
      dispatch({
        type: 'LOAD_PRIORITY_DATA',
        payload: {
          rooms:  ok(rooms),
          flocks: ok(flocks),
        },
      });
      dispatch({ type: 'LOAD_USER_PROFILE', payload: ok(profile, null) });

      // If only priority data requested, stop here
      if (priorityOnly) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      const [eggs, mortality, feed, machines, batches, transfers, chickLosses, notifications, insights, inventory] =
        await secondaryP;

      dispatch({
        type: 'LOAD_SECONDARY_DATA',
        payload: {
          eggRecords:       ok(eggs),
          mortalityRecords: ok(mortality),
          feedRecords:      ok(feed),
          incubators:       ok(machines),
          incubatorBatches: mergeBatchChildren(ok(batches), ok(transfers), ok(chickLosses)),
          notifications:    ok(notifications),
          insights:         ok(insights),
          inventoryItems:   ok(inventory),
        },
      });

      if (financialP) {
        const [sales, expenses] = await financialP;
        dispatch({
          type: 'LOAD_FINANCIAL_DATA',
          payload: {
            sales:    ok(sales),
            expenses: ok(expenses),
          },
        });
      }

      cache.invalidateByTags(Object.values(CACHE_TAGS));
      initialLoadRef.current = true;
    } catch (err) {
      console.error('Failed to load app data:', err);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [canLoadFinancial]);

  // ── Actions factory ────────────────────────────────────────────────────────

  function inv(...tags) {
    if (tags.length) cache.invalidateByTags(tags);
    clearMemoCache();
  }

  const actions = {
    toggleTheme:         () => dispatch({ type: 'TOGGLE_THEME' }),
    selectRoom:          (id) => dispatch({ type: 'SELECT_ROOM', payload: id }),
    selectFlock:         (id) => dispatch({ type: 'SELECT_FLOCK', payload: id }),
    selectInventoryItem: (id) => dispatch({ type: 'SELECT_INVENTORY_ITEM', payload: id }),
    loadAllData,
    resetData: () => {
      dispatch({ type: 'RESET_DATA' });
      clearMemoCache();
    },

    // ── Rooms ───────────────────────────────────────────────────────────────
    addRoom: async (room) => {
      const created = await roomsApi.create(room);
      dispatch({ type: 'ADD_ROOM', payload: created });
      inv(CACHE_TAGS.ROOMS);
      return created;
    },

    updateRoom: async (room) => {
      const updated = await roomsApi.update(room.id, room);
      dispatch({ type: 'UPDATE_ROOM', payload: updated });
      inv(CACHE_TAGS.ROOMS);
      return updated;
    },

    deleteRoom: async (id) => {
      await roomsApi.remove(id);
      dispatch({ type: 'DELETE_ROOM', payload: id });
      inv(CACHE_TAGS.ROOMS, CACHE_TAGS.FLOCKS, CACHE_TAGS.EGGS);
    },

    // ── Flocks ──────────────────────────────────────────────────────────────
    addFlock: async (flock) => {
      const created = await flocksApi.create(flock);
      dispatch({ type: 'ADD_FLOCK', payload: created });
      inv(CACHE_TAGS.FLOCKS);
      return created;
    },

    updateFlock: async (flock) => {
      const updated = await flocksApi.update(flock.id, flock);
      dispatch({ type: 'UPDATE_FLOCK', payload: updated });
      inv(CACHE_TAGS.FLOCKS);
      return updated;
    },

    deleteFlock: async (id) => {
      await flocksApi.remove(id);
      dispatch({ type: 'DELETE_FLOCK', payload: id });
      inv(CACHE_TAGS.FLOCKS, CACHE_TAGS.EGGS);
    },

    // ── Egg Records ─────────────────────────────────────────────────────────
    addEggRecord: async (record) => {
      const created = await eggRecordsApi.create({ ...record, date: record.date || TODAY });
      dispatch({ type: 'ADD_EGG_RECORD', payload: created });
      inv(CACHE_TAGS.EGGS);
      return created;
    },

    deleteEggRecord: async (id) => {
      await eggRecordsApi.remove(id);
      dispatch({ type: 'DELETE_EGG_RECORD', payload: id });
      inv(CACHE_TAGS.EGGS);
    },

    updateEggAllocation: async (payload) => {
      // payload: { id, qtyIncubated, qtySold }
      const record = state.eggRecords.find((e) => e.id === payload.id);
      if (!record) return;
      await eggRecordsApi.update(payload.id, {
        ...record,
        qtyIncubated: (record.qtyIncubated || 0) + (payload.qtyIncubated || 0),
        qtySold:      (record.qtySold      || 0) + (payload.qtySold      || 0),
      });
      dispatch({ type: 'UPDATE_EGG_ALLOCATION', payload });
      inv(CACHE_TAGS.EGGS);
    },

    // ── Mortality ───────────────────────────────────────────────────────────
    addMortalityRecord: async (record) => {
      const created = await mortalityApi.create({ ...record, date: record.date || TODAY });
      dispatch({ type: 'ADD_MORTALITY_RECORD', payload: created });
      // Reload flocks so bird counts (updated by DB trigger) are fresh
      const flocks = await flocksApi.getAll();
      dispatch({ type: 'SET_FLOCKS', payload: flocks });
      inv(CACHE_TAGS.MORTALITY, CACHE_TAGS.FLOCKS);
      return created;
    },

    updateMortalityRecord: async (record) => {
      const updated = await mortalityApi.update(record.id, record);
      dispatch({ type: 'UPDATE_MORTALITY_RECORD', payload: updated });
      const flocks = await flocksApi.getAll();
      dispatch({ type: 'SET_FLOCKS', payload: flocks });
      inv(CACHE_TAGS.MORTALITY, CACHE_TAGS.FLOCKS);
      return updated;
    },

    deleteMortalityRecord: async (id) => {
      await mortalityApi.remove(id);
      dispatch({ type: 'DELETE_MORTALITY_RECORD', payload: id });
      const flocks = await flocksApi.getAll();
      dispatch({ type: 'SET_FLOCKS', payload: flocks });
      inv(CACHE_TAGS.MORTALITY, CACHE_TAGS.FLOCKS);
    },

    // ── Feed ────────────────────────────────────────────────────────────────
    addFeedRecord: async (record) => {
      const created = await feedApi.create({ ...record, date: record.date || TODAY });
      dispatch({ type: 'ADD_FEED_RECORD', payload: created });
      inv(CACHE_TAGS.FEED);
      // Auto-deduct from inventory (silent — failure must not block feed record)
      let deductionResult = null;
      try {
        deductionResult = await inventoryApi.deductFeed({
          feedType: created.feedType,
          qtyKg:    created.qtyKg ?? created.amount ?? 0,
          roomId:   created.roomId ?? null,
          flockId:  created.flockId ?? null,
          date:     created.date,
        });
        const items = await inventoryApi.getAllItems();
        dispatch({ type: 'SET_INVENTORY_ITEMS', payload: items });
        inv(CACHE_TAGS.INVENTORY);
      } catch (_) {
        // Inventory deduction failure is non-fatal
      }
      return { ...created, _deductionResult: deductionResult };
    },

    updateFeedRecord: async (record, originalRecord) => {
      const updated = await feedApi.update(record.id, record);
      dispatch({ type: 'UPDATE_FEED_RECORD', payload: updated });
      inv(CACHE_TAGS.FEED);

      // Adjust inventory to reflect the edit (silent — failure must not block the update)
      if (originalRecord) {
        try {
          const oldQty  = Number(originalRecord.qtyKg ?? originalRecord.amount ?? 0);
          const newQty  = Number(updated.qtyKg ?? updated.amount ?? 0);
          const oldType = originalRecord.feedType;
          const newType = updated.feedType;
          const typeChanged = oldType !== newType;
          // For a type change, delta = full new qty (deduct new); old qty is passed separately for restore.
          const delta = typeChanged ? newQty : newQty - oldQty;

          if (delta !== 0 || typeChanged) {
            await inventoryApi.adjustFeed({
              feedType:         newType,
              quantityDelta:    delta,
              previousFeedType: typeChanged ? oldType : null,
              previousQuantity: typeChanged ? oldQty  : null,
              roomId:           updated.roomId ?? null,
              date:             updated.date,
            });
            const items = await inventoryApi.getAllItems();
            dispatch({ type: 'SET_INVENTORY_ITEMS', payload: items });
            inv(CACHE_TAGS.INVENTORY);
          }
        } catch (_) {
          // Inventory adjustment failure is non-fatal
        }
      }

      return updated;
    },

    deleteFeedRecord: async (record) => {
      await feedApi.remove(record.id);
      dispatch({ type: 'DELETE_FEED_RECORD', payload: record.id });
      inv(CACHE_TAGS.FEED);
      // Restore inventory stock silently — failure must not block deletion
      try {
        const qty = Number(record.qtyKg ?? record.amount ?? 0);
        if (qty > 0 && record.feedType) {
          await inventoryApi.adjustFeed({
            feedType:      record.feedType,
            quantityDelta: -qty,   // negative = restore
            roomId:        record.roomId ?? null,
            date:          record.date,
          });
          const items = await inventoryApi.getAllItems();
          dispatch({ type: 'SET_INVENTORY_ITEMS', payload: items });
          inv(CACHE_TAGS.INVENTORY);
        }
      } catch (_) {
        // Inventory restore failure is non-fatal
      }
    },

    // ── Inventory ───────────────────────────────────────────────────────────
    addInventoryItem: async (item) => {
      const created = await inventoryApi.createItem(item);
      dispatch({ type: 'ADD_INVENTORY_ITEM', payload: created });
      inv(CACHE_TAGS.INVENTORY);
      return created;
    },

    updateInventoryItem: async (item) => {
      const updated = await inventoryApi.updateItem(item.id, item);
      dispatch({ type: 'UPDATE_INVENTORY_ITEM', payload: updated });
      inv(CACHE_TAGS.INVENTORY);
      return updated;
    },

    deleteInventoryItem: async (id) => {
      await inventoryApi.deleteItem(id);
      dispatch({ type: 'DELETE_INVENTORY_ITEM', payload: id });
      inv(CACHE_TAGS.INVENTORY);
    },

    addStockMovement: async (movement) => {
      const created = await inventoryApi.addMovement(movement);
      // Reload items so currentStock + unitPrice reflect latest values
      const items = await inventoryApi.getAllItems();
      dispatch({ type: 'SET_INVENTORY_ITEMS', payload: items });
      inv(CACHE_TAGS.INVENTORY);
      return created;
    },

    // ── Incubator Machines ──────────────────────────────────────────────────
    addIncubator: async (machine) => {
      const created = await incubatorApi.createMachine(machine);
      dispatch({ type: 'ADD_INCUBATOR', payload: created });
      inv(CACHE_TAGS.INCUBATOR);
      return created;
    },

    updateIncubator: async (machine) => {
      const updated = await incubatorApi.updateMachine(machine.id, machine);
      dispatch({ type: 'UPDATE_INCUBATOR', payload: updated });
      inv(CACHE_TAGS.INCUBATOR);
      return updated;
    },

    deleteIncubator: async (id) => {
      await incubatorApi.deleteMachine(id);
      dispatch({ type: 'DELETE_INCUBATOR', payload: id });
      inv(CACHE_TAGS.INCUBATOR);
    },

    refreshIncubators: async () => {
      const machines = await incubatorApi.getAllMachines();
      dispatch({ type: 'SET_INCUBATORS', payload: machines });
    },

    // ── Incubator Batches ───────────────────────────────────────────────────
    addIncubatorBatch: async (batch) => {
      const created = await incubatorApi.createBatch(batch);
      // Backend may return an existing batch (upsert) — dispatch update instead of add to avoid duplicates
      const alreadyExists = state.incubatorBatches.some((b) => b.id === created.id);
      dispatch({ type: alreadyExists ? 'UPDATE_INCUBATOR_BATCH' : 'ADD_INCUBATOR_BATCH', payload: created });
      // Refresh machine status (batch creation may mark incubator as in_use)
      const machines = await incubatorApi.getAllMachines();
      dispatch({ type: 'SET_INCUBATORS', payload: machines });
      inv(CACHE_TAGS.INCUBATOR);
      return created;
    },

    updateIncubatorBatch: async (batch, originalIncubatorId) => {
      const updated = await incubatorApi.updateBatch(batch.id, batch);
      dispatch({ type: 'UPDATE_INCUBATOR_BATCH', payload: updated });
      // Refresh machine status when incubator changes or batch completes
      const incubatorChanged = originalIncubatorId !== batch.incubatorId;
      if (batch.status === 'completed' || incubatorChanged) {
        const machines = await incubatorApi.getAllMachines();
        dispatch({ type: 'SET_INCUBATORS', payload: machines });
      }
      inv(CACHE_TAGS.INCUBATOR);
      return updated;
    },

    deleteIncubatorBatch: async (id) => {
      await incubatorApi.deleteBatch(id);
      dispatch({ type: 'DELETE_INCUBATOR_BATCH', payload: id });
      // Refresh machine status (deleting a batch may free the incubator)
      const machines = await incubatorApi.getAllMachines();
      dispatch({ type: 'SET_INCUBATORS', payload: machines });
      inv(CACHE_TAGS.INCUBATOR);
    },

    addBatchTransfer: async (batchId, transfer) => {
      const created = await incubatorApi.createTransfer({ ...transfer, batchId });
      dispatch({ type: 'ADD_BATCH_TRANSFER', payload: { batchId, transfer: created } });
      // Reload flocks so bird counts (updated by DB trigger) are fresh
      const flocks = await flocksApi.getAll();
      dispatch({ type: 'SET_FLOCKS', payload: flocks });
      inv(CACHE_TAGS.INCUBATOR, CACHE_TAGS.FLOCKS);
      return created;
    },

    addChickLoss: async (batchId, loss) => {
      const created = await incubatorApi.createChickLoss({ ...loss, batchId });
      dispatch({ type: 'ADD_CHICK_LOSS', payload: { batchId, loss: created } });
      inv(CACHE_TAGS.INCUBATOR);
      return created;
    },

    // ── Sales ───────────────────────────────────────────────────────────────
    addSale: async (sale) => {
      const created = await salesApi.create({ ...sale, date: sale.date || TODAY });
      dispatch({ type: 'ADD_SALE', payload: created });
      inv(CACHE_TAGS.SALES);
      if (sale.type === 'bird') {
        // Reload flocks so counts (updated by DB trigger) are fresh
        const flocks = await flocksApi.getAll();
        dispatch({ type: 'SET_FLOCKS', payload: flocks });
        inv(CACHE_TAGS.FLOCKS);
      }
      if (sale.type === 'egg') {
        const eggs = await eggRecordsApi.getAll();
        dispatch({ type: 'SET_EGG_RECORDS', payload: eggs });
        inv(CACHE_TAGS.EGGS);
      }
      return created;
    },

    editSale: async (sale) => {
      const updated = await salesApi.update(sale.id, sale);
      dispatch({ type: 'EDIT_SALE', payload: updated });
      inv(CACHE_TAGS.SALES);
      if (sale.type === 'bird') {
        const flocks = await flocksApi.getAll();
        dispatch({ type: 'SET_FLOCKS', payload: flocks });
        inv(CACHE_TAGS.FLOCKS);
      }
      if (sale.type === 'egg') {
        const eggs = await eggRecordsApi.getAll();
        dispatch({ type: 'SET_EGG_RECORDS', payload: eggs });
        inv(CACHE_TAGS.EGGS);
      }
      return updated;
    },

    deleteSale: async (id) => {
      const saleToDelete = state.sales.find((s) => s.id === id);
      await salesApi.remove(id);
      dispatch({ type: 'DELETE_SALE', payload: id });
      inv(CACHE_TAGS.SALES);
      if (saleToDelete?.type === 'bird') {
        const flocks = await flocksApi.getAll();
        dispatch({ type: 'SET_FLOCKS', payload: flocks });
        inv(CACHE_TAGS.FLOCKS);
      }
      if (saleToDelete?.type === 'egg') {
        const eggs = await eggRecordsApi.getAll();
        dispatch({ type: 'SET_EGG_RECORDS', payload: eggs });
        inv(CACHE_TAGS.EGGS);
      }
    },

    // ── Expenses ────────────────────────────────────────────────────────────
    addExpense: async (expense) => {
      const created = await expensesApi.create({ ...expense, date: expense.date || TODAY });
      dispatch({ type: 'ADD_EXPENSE', payload: created });
      inv(CACHE_TAGS.EXPENSES);
      return created;
    },

    updateExpense: async (id, expense) => {
      const updated = await expensesApi.update(id, expense);
      dispatch({ type: 'UPDATE_EXPENSE', payload: updated });
      inv(CACHE_TAGS.EXPENSES);
      return updated;
    },

    deleteExpense: async (id) => {
      await expensesApi.remove(id);
      dispatch({ type: 'DELETE_EXPENSE', payload: id });
      inv(CACHE_TAGS.EXPENSES);
    },

    // ── Notifications ────────────────────────────────────────────────────────
    markNotificationRead: async (id) => {
      await notificationsApi.markRead(id);
      dispatch({ type: 'MARK_NOTIFICATION_READ', payload: id });
      inv(CACHE_TAGS.NOTIFICATIONS);
    },

    markAllNotificationsRead: async () => {
      await notificationsApi.markAllRead();
      dispatch({ type: 'MARK_ALL_NOTIFICATIONS_READ' });
      inv(CACHE_TAGS.NOTIFICATIONS);
    },

    removeNotification: async (id) => {
      await notificationsApi.remove(id);
      dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
      inv(CACHE_TAGS.NOTIFICATIONS);
    },

    // ── Insights ───────────────────────────────────────────────────────────────
    refreshInsights: async () => {
      const data = await insightsApi.getActive(7);
      dispatch({ type: 'LOAD_INSIGHTS', payload: data });
      return data;
    },

    generateInsights: async () => {
      const result = await insightsApi.generate();
      // Refresh insights after generation
      const data = await insightsApi.getActive(7);
      dispatch({ type: 'LOAD_INSIGHTS', payload: data });
      return result;
    },

    markInsightNotified: (id) => {
      dispatch({ type: 'MARK_INSIGHT_NOTIFIED', payload: id });
    },
  };

  return <AppContext.Provider value={{ state, actions }}>{children}</AppContext.Provider>;
}

// Memoized selector hook for better performance
export function useAppState(selector = (state) => state) {
  const { state } = useContext(AppContext);
  return useMemo(() => selector(state), [state, selector]);
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');

  // Memoize the context value to prevent unnecessary re-renders
  const { state, actions } = ctx;
  const memoizedValue = useMemo(() => ({ state, actions }), [state, actions]);
  return memoizedValue;
}
